"use server";

// Server actions backing the sign-in and sign-up forms.
//
// Both actions return a `{ error }` shape on failure (consumed by the form
// pages to surface a banner) and trigger a `redirect()` on success. The
// redirect throws a special internal Next.js error which must NOT be caught
// — we let it propagate. Real authorization errors are caught and returned.
//
// `redirect()` flow: Next.js's server-action runtime intercepts the thrown
// `NEXT_REDIRECT` error and converts it into the response, so the action's
// return type is `void` on the redirect branch and `{ error }` otherwise.

import { redirect } from "next/navigation";
import { hash } from "@node-rs/argon2";
import { eq } from "drizzle-orm";
import { z } from "zod";
import { AuthError } from "next-auth";
import { signIn } from "./config";
import { db, adminDb } from "../db";
import * as schema from "@aio/platform-db";

const signupSchema = z
  .object({
    email: z.string().email("Enter a valid email address"),
    password: z.string().min(8, "Minimum 8 characters"),
    confirmPassword: z.string(),
    name: z.string().min(1, "Required").max(120),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

const signinSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Required"),
});

/**
 * Convert a free-form name into a URL-safe slug. Folds Unicode diacritics
 * (Serbian Latin characters like š/đ/č/ć/ž normalise to s/d/c/c/z),
 * lowercases, replaces non-alphanumeric runs with single hyphens, and
 * trims hyphens from the ends. Empty inputs collapse to "user".
 */
function slugify(input: string): string {
  const normalised = input
    .normalize("NFD")
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalised.length > 0 ? normalised : "user";
}

/**
 * Pick a slug that doesn't yet exist in the `companies` table. Tries the
 * raw base first, then `<base>-2`, `<base>-3`, etc. The loop is bounded —
 * after 50 attempts it falls back to a UUID suffix so a runaway collision
 * (extremely unlikely with the per-user namespace) cannot stall signup.
 *
 * Uses `adminDb` (BYPASSRLS) because `companies` is RLS-enforced — without a
 * tenant context the regular pool would return zero rows for every slug,
 * making duplicate detection falsely negative. The signup flow predates a
 * tenant selection so there is no `app.current_company_id` to set.
 */
async function pickAvailableCompanySlug(base: string): Promise<string> {
  const existing = await adminDb
    .select({ slug: schema.companies.slug })
    .from(schema.companies)
    .where(eq(schema.companies.slug, base))
    .limit(1);
  if (existing.length === 0) return base;

  for (let suffix = 2; suffix <= 50; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    const hit = await adminDb
      .select({ slug: schema.companies.slug })
      .from(schema.companies)
      .where(eq(schema.companies.slug, candidate))
      .limit(1);
    if (hit.length === 0) return candidate;
  }
  return `${base}-${crypto.randomUUID().slice(0, 8)}`;
}

export interface AuthActionState {
  error?: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Sign-up server action. Validates, hashes the password with argon2id,
 * creates the user + a "Personal" company + an active membership atomically
 * (single transaction), then signs the user in via the credentials provider
 * and redirects to their new workspace.
 *
 * On any validation or DB error returns a `{ error, fieldErrors }` payload
 * for the form to render. Auth.js's `signIn(...)` throws an `AuthError`
 * subclass on failure which is also normalised into the same shape.
 */
export async function signupAction(
  _prev: AuthActionState | undefined,
  formData: FormData,
): Promise<AuthActionState> {
  const raw = {
    email: String(formData.get("email") ?? "").trim(),
    password: String(formData.get("password") ?? ""),
    confirmPassword: String(formData.get("confirmPassword") ?? ""),
    name: String(formData.get("name") ?? "").trim(),
  };

  const parsed = signupSchema.safeParse(raw);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const path = issue.path[0];
      if (typeof path === "string" && fieldErrors[path] === undefined) {
        fieldErrors[path] = issue.message;
      }
    }
    return { error: "Please correct the highlighted fields.", fieldErrors };
  }

  const { email, password, name } = parsed.data;

  // Reject duplicate emails up-front with a friendly message rather than
  // letting the unique-constraint fire and surface a generic DB error.
  const existing = await db
    .select({ id: schema.users.id })
    .from(schema.users)
    .where(eq(schema.users.email, email))
    .limit(1);
  if (existing.length > 0) {
    return {
      error: "An account with this email already exists.",
      fieldErrors: { email: "Email already registered" },
    };
  }

  const passwordHash = await hash(password);
  const baseSlug = `${slugify(name)}-personal`;
  const companySlug = await pickAvailableCompanySlug(baseSlug);

  // Wrap user + company + membership in one transaction so a partial
  // failure (e.g. the membership insert blowing up after a company is
  // created) does not leave orphaned rows. Drizzle's transaction returns
  // whatever the callback returns.
  //
  // The transaction uses `adminDb` (BYPASSRLS) because the `companies` and
  // `company_memberships` inserts would otherwise be rejected by their RLS
  // policies — `WITH CHECK (id = current_setting('app.current_company_id')
  // ::uuid)` evaluates `NULL = NULL` (false) when no session var is set, so
  // the row is denied. Wrapping in a tenant context isn't possible either:
  // the company doesn't yet exist when we're trying to create it. The user
  // insert could run on the regular pool (users isn't RLS-enforced) but
  // sharing a single transaction across two pools is impossible without
  // distributed-transaction wiring, so the whole atomic block lives on
  // `adminDb`. Switching pools is a one-line change to revisit if/when a
  // future change introduces RLS on users.
  await adminDb.transaction(async (tx) => {
    const [user] = await tx
      .insert(schema.users)
      .values({ email, name, passwordHash })
      .returning({ id: schema.users.id });
    if (!user) throw new Error("signup: user insert returned no row");

    const [company] = await tx
      .insert(schema.companies)
      .values({ slug: companySlug, name: `${name}'s workspace` })
      .returning({ id: schema.companies.id });
    if (!company) throw new Error("signup: company insert returned no row");

    await tx.insert(schema.companyMemberships).values({
      userId: user.id,
      companyId: company.id,
      status: "active",
    });
  });

  // Sign the new user in via the credentials provider. `redirect: false`
  // returns control to this action so we can issue the explicit redirect
  // ourselves — Auth.js's default redirect points at `/` which is wrong
  // for the just-created workspace.
  try {
    await signIn("credentials", { email, password, redirect: false });
  } catch (err) {
    if (err instanceof AuthError) {
      return {
        error:
          "Account was created but automatic sign-in failed. Please sign in manually.",
      };
    }
    throw err;
  }

  redirect(`/demo/${companySlug}`);
}

/**
 * Sign-in server action. Auth.js's `signIn(...)` throws on failure (e.g.
 * `CredentialsSignin` for a bad password). We catch known AuthError
 * subclasses and surface a generic message — leaking whether the email or
 * the password specifically was wrong is bad practice (account-enumeration).
 *
 * On success we look up the user's first company membership and redirect
 * there. If none exists we send the user to `/` (this happens for users
 * created out-of-band, e.g. seed data; the real flow always creates a
 * default company at signup).
 */
export async function signinAction(
  _prev: AuthActionState | undefined,
  formData: FormData,
): Promise<AuthActionState> {
  const raw = {
    email: String(formData.get("email") ?? "").trim(),
    password: String(formData.get("password") ?? ""),
  };

  const parsed = signinSchema.safeParse(raw);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const path = issue.path[0];
      if (typeof path === "string" && fieldErrors[path] === undefined) {
        fieldErrors[path] = issue.message;
      }
    }
    return { error: "Please correct the highlighted fields.", fieldErrors };
  }

  const { email, password } = parsed.data;

  try {
    await signIn("credentials", { email, password, redirect: false });
  } catch (err) {
    if (err instanceof AuthError) {
      return { error: "Invalid email or password." };
    }
    throw err;
  }

  // Resolve the user's first active company so the redirect lands somewhere
  // useful. The userId lookup is by email here because the action does not
  // yet have the session — we just authenticated and the cookie hasn't been
  // round-tripped to the server.
  //
  // Uses `adminDb` (BYPASSRLS) because the join walks `companies` and
  // `company_memberships`, both RLS-enforced. With no `app.current_company_id`
  // session var set yet (we're still resolving WHICH company to send the
  // user to), the regular pool would filter every row out and the redirect
  // would always fall through to `/`. The query is hand-audited tenant-safe:
  // it filters by `users.email` against the just-authenticated email, so a
  // rogue session can't enumerate other users' memberships through it.
  const rows = await adminDb
    .select({ slug: schema.companies.slug })
    .from(schema.companies)
    .innerJoin(
      schema.companyMemberships,
      eq(schema.companies.id, schema.companyMemberships.companyId),
    )
    .innerJoin(schema.users, eq(schema.users.id, schema.companyMemberships.userId))
    .where(eq(schema.users.email, email))
    .limit(1);

  if (rows[0]) {
    redirect(`/demo/${rows[0].slug}`);
  }
  redirect("/");
}
